import React, { createContext, useState, useContext, useRef, useCallback, ReactNode, useEffect } from 'react';
import { debounce } from 'lodash';
import ConversationPreviewTooltip from './ConversationPreviewTooltip';

// Context for tooltip state and handlers
interface TooltipContextType {
  showTooltip: (event: React.MouseEvent, conversationId: string, customAnchorEl?: HTMLElement) => void;
  hideTooltip: () => void;
  pauseTooltipHiding: () => void;
  resumeTooltipHiding: () => void;
}

const TooltipContext = createContext<TooltipContextType>({
  showTooltip: () => { },
  hideTooltip: () => { },
  pauseTooltipHiding: () => { },
  resumeTooltipHiding: () => { }
});

// Hook to use the tooltip context
export const useTooltip = () => useContext(TooltipContext);

interface ConversationTooltipManagerProps {
  children: ReactNode;
}

export const ConversationTooltipManager: React.FC<ConversationTooltipManagerProps> = ({ children }) => {
  // State for tooltip visibility
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // A ref to track if the mouse is hovering the tooltip area
  // (either the card/row or the tooltip itself)
  const isHoveringRef = useRef(false);

  // Flag to temporarily pause tooltip hiding (when hovering tooltip itself)
  const shouldDelayHidingRef = useRef(false);

  // Debounced function to hide tooltip
  const hideTooltipDebounced = useCallback(
    debounce(() => {
      // Only hide if not hovering anything tooltip-related
      if (!isHoveringRef.current && !shouldDelayHidingRef.current) {
        setActiveConversationId(null);
        setAnchorEl(null);
      }
    }, 300),
    []
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      hideTooltipDebounced.cancel();
    };
  }, [hideTooltipDebounced]);

  // Show tooltip handler - called when hovering a card or row
  const showTooltip = useCallback((event: React.MouseEvent, conversationId: string, customAnchorEl?: HTMLElement) => {
    isHoveringRef.current = true;
    hideTooltipDebounced.cancel();

    // Validate the anchor element before setting it
    const target = customAnchorEl || event.currentTarget as HTMLElement;

    // Only set the anchor if it's a valid DOM element that's in the document
    if (target && target.isConnected) {
      setActiveConversationId(conversationId);
      setAnchorEl(target);
    }
  }, [hideTooltipDebounced]);

  // Hide tooltip handler - called when leaving a card or row
  const hideTooltip = useCallback(() => {
    isHoveringRef.current = false;
    hideTooltipDebounced();
  }, [hideTooltipDebounced]);

  // Pause tooltip hiding - called when entering the tooltip itself
  const pauseTooltipHiding = useCallback(() => {
    shouldDelayHidingRef.current = true;
    hideTooltipDebounced.cancel();
  }, [hideTooltipDebounced]);

  // Resume tooltip hiding - called when leaving the tooltip
  const resumeTooltipHiding = useCallback(() => {
    shouldDelayHidingRef.current = false;
    hideTooltipDebounced();
  }, [hideTooltipDebounced]);

  // Context value
  const contextValue = {
    showTooltip,
    hideTooltip,
    pauseTooltipHiding,
    resumeTooltipHiding
  };

  // Check if the anchor element is still valid before rendering the tooltip
  const isAnchorValid = anchorEl && anchorEl.isConnected && document.body.contains(anchorEl);

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}

      {/* Render the tooltip if we have both an active conversation and a valid anchor */}
      {activeConversationId && isAnchorValid && (
        <ConversationPreviewTooltip
          conversationId={activeConversationId}
          anchorEl={anchorEl}
          open={Boolean(activeConversationId)}
          onClose={() => {
            setActiveConversationId(null);
            setAnchorEl(null);
          }}
          onMouseEnter={pauseTooltipHiding}
          onMouseLeave={resumeTooltipHiding}
        />
      )}
    </TooltipContext.Provider>
  );
};

export default ConversationTooltipManager;
